import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import { useSelector } from 'react-redux';
import PDFReportPreview from './PDFReportPreview';
import { ModalDefaultDraggable } from '../../../molecules';
import { useUpdateFormMarkupMutation } from '../../../redux/rtkQuery/apiFormbuilderSlice';
import TabsBar from './TabsBar';
import { RootState } from '../../../redux/root';
import useSelectedFormImages from '../useSelectedFormImages';
import { dispatch } from '../../../redux/store';
import { setSaveIsDisabled, setSelectedForm, setSortedItems } from '../../../redux/slices/thresholdFormSlice';
import { getSortedItems } from './thresholdFormUtils';
import { FORM_FONT_KEY, SIGNATURE, TEMPLATE_KEY, fieldCount, formFontPt } from './thresholdConstants';
import FormBuilderCanvas from './builder/FormBuilderCanvas';
import FieldToolbar from './builder/FieldToolbar';
import FieldPropertiesPanel from './builder/FieldPropertiesPanel';
import { resolveInitialTemplate } from './initialTemplate';
import { markAllOptions } from '../docOvelayTool';

// Re-exported for backward compatibility (EditedLabel / legacy control components).
export { SIGNATURE, TEMPLATE_KEY } from './thresholdConstants';

type templatePage = {};
type templateDataType = { [tab: string]: templatePage };

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2;
const ZOOM_STEP = 0.1;

/**
 * Where a form's unsaved work-in-progress is stashed.
 *
 * Scoped to the form. `TEMPLATE_KEY` alone is one global slot shared by every
 * form, so a stash left behind by form A was restored on top of form B and then
 * saved over it — the same silent wholesale overwrite, across forms. Nothing
 * reads these keys but this popup, and a stale scoped key is inert: it can only
 * ever be offered back to the form it came from.
 */
const templateKeyFor = (formId: number | string) => `${TEMPLATE_KEY}:${formId}`;

/**
 * The unsaved work-in-progress template for `formId`, or null when there isn't one.
 *
 * A template with no FIELDS is not a template. This has to count placements
 * rather than root keys, because the shape that caused the bug is not `{}` —
 * the builder seeds one entry per page image, so a form that reached the
 * clean-slate branch stashed `{"0":{},"1":{},"2":{},"3":{}}`. That has four
 * keys, so a root-key emptiness check waves it through; it is then restored in
 * preference to the form's real markup, and the builder shows
 * "Show all fields (0)" over a form with dozens of fields. It stays that way
 * until localStorage is cleared by hand, because every render re-persists it.
 *
 * Discarding a fieldless stash loses nothing: `onClose` already clears the key,
 * so one can only survive an abnormal exit (tab closed, navigation away), and
 * what it holds is "no fields placed" — exactly what reloading the form gives.
 *
 * Also tolerates malformed JSON: a corrupt value used to throw straight out of
 * the effect and take the whole popup with it.
 */
const readStashedTemplate = (formId?: number) => {
  if (!formId) {
    return null;
  }
  try {
    const parsed = JSON.parse(localStorage.getItem(templateKeyFor(formId)));
    if (!parsed || typeof parsed !== 'object' || fieldCount(parsed) === 0) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const GenerateTemplatePopup = ({ show, setShow }) => {
  const [data, _setData] = useState<templateDataType>({} as templateDataType);
  const [tab, setTab] = useState<string>('0');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [zoom, setZoom] = useState<number>(1);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [previewUri, setPreviewUri] = useState<string>('');
  const [showError, setShowError] = useState<boolean>(false);
  const [updateFormMarkup] = useUpdateFormMarkupMutation();
  const { selectedForm, saveIsDisabled } = useSelector((s: RootState) => s.thresholdForms);
  // selectedForm arrives from the forms LIST, which no longer carries page
  // images; this fetches them for the form in hand.
  const { imagesLoading, savedMarkup, markupLoaded } = useSelectedFormImages();

  const formId = selectedForm?.id;

  const setData = arg => {
    _setData(arg);
    if (formId) {
      localStorage.setItem(templateKeyFor(formId), JSON.stringify(arg));
    }
  };

  useEffect(() => {
    // The old single-slot stash. Left behind it would be indistinguishable from
    // a current one for whichever form happened to open next, so drop it once.
    localStorage.removeItem(TEMPLATE_KEY);
  }, []);

  useEffect(() => {
    if (readStashedTemplate(formId)) {
      dispatch(setSaveIsDisabled(false));
    }
  }, [formId]);

  // Deselect the active field when switching pages.
  useEffect(() => {
    setSelectedKey(null);
  }, [tab]);

  // 1. clean slate - no localStorage and no formMarkup
  // 2. no localStorage and formMarkup exist
  // 3. localStorage exist and formMarkupExist (localStorage wins)
  useEffect(() => {
    // Wait for the page images. Seeding case 1 without them yields ZERO page
    // entries, and setData persists that to localStorage — which the next run
    // then restores as a valid template, latching the builder at no pages.
    if (selectedForm && !imagesLoading) {
      if (Object.keys(data).length === 0) {
        // `fileBase64` can be null when the backend has no readable page files
        // for the form (get_file_base64 returns None) — matches TabsBar's
        // `fileBase64 &&` check.
        const resolved = resolveInitialTemplate({
          stashed: readStashedTemplate(formId),
          rowMarkup: selectedForm.formMarkup,
          serverMarkup: savedMarkup,
          markupLoaded,
          pageCount: (selectedForm.fileBase64 || []).length,
        });
        if (resolved) {
          setData(resolved.template);
          if (resolved.enableSave) {
            dispatch(setSaveIsDisabled(false));
          }
        }
      }
    }
  }, [selectedForm, imagesLoading, savedMarkup, markupLoaded]);

  const isSignatureExists = () => {
    let found = false;
    Object.keys(data).forEach(pageIndex => {
      const page = data[pageIndex];
      if (Object.keys(page).indexOf(SIGNATURE) > -1) {
        found = true;
      }
    });
    return found;
  };

  const onSave = async () => {
    if (!isSignatureExists()) {
      setShowError(true);
      return;
    }

    // Refuse to silently replace a populated form with a near-empty one.
    //
    // Saving overwrites form_markup wholesale, with no server-side history to
    // fall back on. A form that failed to load its fields — for any reason —
    // therefore looked like a form with no fields, and one Save destroyed the
    // real layout. That happened: 46 placements on Threshold Concrete were
    // replaced by a single signature. Making the user confirm costs one click
    // and makes the failure recoverable instead of terminal.
    const saved = fieldCount(selectedForm?.formMarkup);
    const next = fieldCount(data);
    if (saved > 0 && next < saved / 2) {
      // eslint-disable-next-line no-alert, no-restricted-globals
      const proceed = window.confirm(
        `This form currently has ${saved} field(s) placed, but you are about to save ${next}. ` +
          `${saved - next} field(s) would be permanently removed.

` +
          `If the page looks empty, close this window without saving and reopen the form — ` +
          `the fields may simply not have loaded.

Save anyway?`,
      );
      if (!proceed) {
        return;
      }
    }

    setShowError(false);
    const formData = new FormData();
    formData.append('form_markup', JSON.stringify(data));
    await updateFormMarkup({ id: selectedForm.id, data: formData });
    localStorage.removeItem(templateKeyFor(selectedForm.id));
    dispatch(setSaveIsDisabled(true));

    const _sorted = getSortedItems(data, tab);
    if (_sorted && _sorted.length > 0) {
      dispatch(setSortedItems(_sorted));
    }
  };

  const onClose = () => {
    setShow(false);
    // Before `setSelectedForm(undefined)` — after it there is no id to key on
    // and the stash would outlive the form it belongs to.
    if (formId) {
      localStorage.removeItem(templateKeyFor(formId));
    }
    dispatch(setSelectedForm(undefined));
    _setData({} as templateDataType);
    dispatch(setSaveIsDisabled(true));
    setSelectedKey(null);
    setShowPreview(false);
    setPreviewUri('');
    setZoom(1);
  };

  const clampZoom = (z: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 10) / 10));

  const closePreview = () => {
    setShowPreview(false);
    setPreviewUri('');
  };

  // Open the generated PDF full-size in a new tab. Synchronous window.open with
  // no noopener so the tab can resolve the opener's blob URL.
  const openPreviewInNewTab = () => {
    if (previewUri) window.open(previewUri, '_blank');
  };

  if (!show || !selectedForm) {
    return null;
  }

  return (
    <>
      <ModalDefaultDraggable
        title={`Form: ${selectedForm.name}`}
        onClose={onClose}
        maxWidth="1180px"
        onClickAwayClose={!showPreview}
      >
        <Toolbar>
          <ZoomGroup>
            <ZoomBtn type="button" onClick={() => setZoom(z => clampZoom(z - ZOOM_STEP))} aria-label="Zoom out">
              <ZoomOutIcon style={{ fontSize: 18 }} />
            </ZoomBtn>
            <ZoomValue>{Math.round(zoom * 100)}%</ZoomValue>
            <ZoomBtn type="button" onClick={() => setZoom(z => clampZoom(z + ZOOM_STEP))} aria-label="Zoom in">
              <ZoomInIcon style={{ fontSize: 18 }} />
            </ZoomBtn>
          </ZoomGroup>
          {/* The form's text size. Stored at the ROOT of the markup, beside
              `completed`, so it needs no backend change and reaches inspections
              that already exist through the template rebase. */}
          <FontGroup>
            <FontLabel htmlFor="form-font-pt">Text size</FontLabel>
            <FontInput
              id="form-font-pt"
              value={formFontPt(data)}
              inputMode="numeric"
              onChange={e => {
                const pt = Number(e.target.value);
                if (!Number.isFinite(pt) || pt <= 0) return;
                setData({ ...data, [FORM_FONT_KEY]: `${pt}` });
                dispatch(setSaveIsDisabled(false));
              }}
              title="Applies to every field that has not been given its own size"
            />
            <FontUnit>pt</FontUnit>
          </FontGroup>
          <Spacer />
          <OutlineBtn type="button" onClick={() => setShowPreview(true)}>
            Preview PDF
          </OutlineBtn>
          <SaveBtn type="button" onClick={onSave} disabled={saveIsDisabled}>
            Save
          </SaveBtn>
        </Toolbar>

        {showError && <ErrorBar>A signature must be added to the form before saving.</ErrorBar>}

        <Body>
          <Left>
            <TabsBar tab={tab} setTab={setTab} />
            <FormBuilderCanvas
              data={data}
              setData={setData}
              tab={tab}
              pages={selectedForm.fileBase64 as any}
              selectedKey={selectedKey}
              setSelectedKey={setSelectedKey}
              zoom={zoom}
            />
            <Hint>Drag to move, drag a corner to resize. Arrows nudge 1 mm (Shift = 10 mm).</Hint>
          </Left>
          <Right>
            <FieldToolbar data={data} setData={setData} tab={tab} setSelectedKey={setSelectedKey} />
            <FieldPropertiesPanel
              data={data}
              setData={setData}
              tab={tab}
              selectedKey={selectedKey}
              setSelectedKey={setSelectedKey}
            />
          </Right>
        </Body>
      </ModalDefaultDraggable>

      {showPreview && (
        <ModalDefaultDraggable title="Report preview" onClose={closePreview} wide maxHeight="92vh" zIndex={20}>
          <PreviewShell>
            <PreviewCaption>
              How the stamped form will print — fields at their set positions and sizes.
            </PreviewCaption>
            <PreviewViewport>
              {/* Every option ticked: this is a placeholder render, the same
                  reason text fields show their '-test' value. A group is stored
                  unticked, and an unticked option stamps nothing, so without
                  this a group the admin had just added was invisible here. */}
              <PDFReportPreview data={markAllOptions(data)} onReady={setPreviewUri} />
            </PreviewViewport>
            <PreviewFooter>
              <OutlineBtn type="button" onClick={openPreviewInNewTab} disabled={!previewUri}>
                Open in new tab
              </OutlineBtn>
              <SaveBtn type="button" onClick={closePreview}>
                Done
              </SaveBtn>
            </PreviewFooter>
          </PreviewShell>
        </ModalDefaultDraggable>
      )}
    </>
  );
};

export default GenerateTemplatePopup;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const FontGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const FontLabel = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--neutral-600);
  white-space: nowrap;
`;

const FontInput = styled.input`
  width: 56px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  font-size: 0.8125rem;
  color: var(--mainText);
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const FontUnit = styled.span`
  font-size: 0.75rem;
  color: var(--neutral-500);
`;

const Spacer = styled.div`
  flex: 1;
`;

const ZoomGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-50);
`;

const ZoomBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--neutral-600);
  cursor: pointer;

  &:hover {
    background: var(--primary-50);
    color: var(--primary-700);
  }
`;

const ZoomValue = styled.span`
  min-width: 44px;
  text-align: center;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--neutral-700);
`;

const OutlineBtn = styled.button`
  height: 40px;
  padding: 0 18px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--neutral-700);
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--primary-50);
    border-color: var(--primary-600);
    color: var(--primary-700);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PreviewShell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const PreviewCaption = styled.div`
  font-size: 0.8125rem;
  color: var(--neutral-600);
`;

const PreviewViewport = styled.div`
  height: 70vh;
  min-height: 420px;
  border: 1px solid var(--border);
  border-radius: 10px;
  overflow: hidden;
  background: var(--neutral-50);
  box-shadow: var(--shadow-xs);
`;

const PreviewFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const SaveBtn = styled.button`
  min-width: 120px;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--primary-700);
  }
  &:disabled {
    background: var(--neutral-300);
    cursor: not-allowed;
  }
`;

const ErrorBar = styled.div`
  margin-bottom: 12px;
  padding: 10px 14px;
  border: 1px solid var(--danger-500, #d44b3f);
  border-radius: 8px;
  background: var(--danger-50, #fdecea);
  color: var(--danger-700, #b83a30);
  font-size: 0.8125rem;
`;

const Body = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;

  @media only screen and (max-width: 900px) {
    flex-direction: column;
  }
`;

const Left = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Right = styled.div`
  flex: 0 0 340px;
  width: 340px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media only screen and (max-width: 900px) {
    flex: 1 1 auto;
    width: 100%;
  }
`;

const Hint = styled.div`
  font-size: 0.75rem;
  color: var(--neutral-500);
`;
